import { useThemeContextSelector } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Surface } from "../Surface";

export type PanelProps = {
    spacing?: "medium" | "small";
    noShadow?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const Panel = ({
    ref,
    style,
    children,
    spacing,
    noShadow,
    ...rest
}: PanelProps & {
    ref?: React.RefObject<HTMLDivElement>;
}) => {
    const panelStyle = {
        padding: "8px",
        ...(spacing === "small" && { padding: "0.8px" }),
        ...style,
    };

    return (
        <div ref={ref} {...rest}>
            <Surface style={panelStyle} withShadow={!noShadow}>
                {children}
            </Surface>
        </div>
    );
};

Panel.displayName = "Panel";

export const PanelDivider = ({
    ref,
    style,
    children,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    ref: React.RefObject<HTMLDivElement>;
}) => {
    const dividerStyle = {
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
        marginBottom: "8px",
        paddingBottom: "8px",
        ...style,
    };

    return (
        <div style={dividerStyle} {...rest} ref={ref}>
            {children}
        </div>
    );
};

PanelDivider.displayName = "PanelDivider";

export const PanelHeader = ({
    ref,
    style,
    children,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    ref: React.RefObject<HTMLDivElement>;
}) => {
    const headerStyle = {
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
        fontSize: "14px",
        marginBottom: "8px",
        paddingBottom: "8px",
        ...style,
    };

    return (
        <div style={headerStyle} {...rest} ref={ref}>
            {children}
        </div>
    );
};

PanelHeader.displayName = "PanelHeader";

export const PanelSection = ({
    ref,
    style,
    children,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    ref: React.RefObject<HTMLDivElement>;
}) => {
    const sectionStyle = {
        marginTop: "16px",
        "&:first-of-type": {
            marginTop: "4px",
        },
        ...style,
    };

    return (
        <Box sx={sectionStyle} {...rest} ref={ref}>
            {children}
        </Box>
    );
};

PanelSection.displayName = "PanelSection";

export const PanelHeadline = ({
    ref,
    style,
    children,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    ref: React.RefObject<HTMLDivElement>;
}) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);

    const headlineStyle = {
        color: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
        fontSize: "12px",
        fontWeight: 500,
        marginBottom: "8px",
        marginLeft: "6px",
        ...style,
    };

    return (
        <div style={headlineStyle} {...rest} ref={ref}>
            {children}
        </div>
    );
};

PanelHeadline.displayName = "PanelHeadline";

export const PanelFooter = ({
    ref,
    style,
    children,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    ref: React.RefObject<HTMLDivElement>;
}) => {
    const footerStyle = {
        borderTop: "1px solid rgba(0, 0, 0, 0.1)",
        fontSize: "14px",
        marginTop: "8px",
        paddingTop: "8px",
        ...style,
    };

    return (
        <div style={footerStyle} {...rest} ref={ref}>
            {children}
        </div>
    );
};

PanelFooter.displayName = "PanelFooter";
